import { useState, useRef } from 'react';
import { Upload, CheckCircle, AlertCircle, Image, Loader2, Store, Ticket, Calendar, User, Phone, Mail, FileText, Gift, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { smartCompressImage, formatSize } from '../lib/imageCompression';

interface FormData {
  region: 'local' | 'mainland';
  restaurantName: string;
  title: string;
  description: string;
  redeemInstructions: string;
  validUntil: string;
  maxClaims: string;
  merchantVerifyCode: string;
  submitterName: string;
  submitterPhone: string;
  submitterEmail: string;
}

const EMPTY_FORM: FormData = {
  region: 'local',
  restaurantName: '',
  title: '',
  description: '',
  redeemInstructions: '',
  validUntil: '',
  maxClaims: '',
  merchantVerifyCode: '',
  submitterName: '',
  submitterPhone: '',
  submitterEmail: '',
};

interface Template {
  id: string;
  label: string;
  content: string;
}

const DESCRIPTION_TEMPLATES: Template[] = [
  {
    id: 'hk-detailed',
    label: '香港餐廳（詳細）',
    content: `\`店舖簡介\`
（請簡介餐廳特色、招牌菜式、裝修風格）

\`優惠內容\`
凡惠顧滿 $XXX，出示本福利圖，即可享 XXXX。
不可與其他優惠同時使用，優惠最終解釋權歸店鋪所有。

\`📍店舖地址：\`
（請填寫完整地址）

\`⏰ 營業時間：\`
（請填寫營業時間）`,
  },
  {
    id: 'hk-short',
    label: '香港餐廳（簡短）',
    content: `凡到店出示本福利圖，即可享 XXXX。
不可與其他優惠同時使用，優惠最終解釋權歸店舖所有。

\`📍店舖地址：\`
（請填寫完整地址）`,
  },
  {
    id: 'mainland',
    label: '內地餐廳',
    content: `\`店舖簡介\`
（請簡介餐廳特色、招牌菜式）

\`優惠內容\`
凡惠顧任何一碗拉麵 / 套餐，出示本福利圖，即可免費獲贈 XXXX。
每單限贈一份，不可與其他優惠同時使用，優惠最終解釋權歸店鋪所有。

\`店鋪地址：\`
（請填寫完整地址）`,
  },
];

const REDEEM_TEMPLATES: Template[] = [
  {
    id: 'default',
    label: '預設版',
    content: '預設：出示此訊息同兌換碼畀店員，即可享用優惠。',
  },
  {
    id: 'detailed',
    label: '詳細版',
    content: `出示此訊息同兌換碼畀店員，即可享用優惠。
- 只適用堂食
- 不可與店內其他折扣、套餐同時使用
- 優惠最終解釋權歸店鋪所有`,
  },
];

interface PendingTemplate {
  field: keyof FormData;
  content: string;
}

export default function MerchantSubmitOffer() {
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [compressInfo, setCompressInfo] = useState<{ originalSize: number; compressedSize: number; ratio: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<PendingTemplate | null>(null);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const applyTemplate = (field: keyof FormData, content: string, mode: 'replace' | 'append') => {
    setForm((prev) => {
      const current = prev[field] || '';
      const trimmed = current.trim();
      let next = content;
      if (mode === 'append' && trimmed) {
        next = `${trimmed}\n\n${content}`;
      }
      return { ...prev, [field]: next };
    });
    setPendingTemplate(null);
  };

  const onTemplateClick = (field: keyof FormData, content: string) => {
    const current = form[field]?.trim() || '';
    if (!current) {
      applyTemplate(field, content, 'replace');
      return;
    }
    setPendingTemplate({ field, content });
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setUploading(true);
    setCompressInfo(null);

    try {
      // 優惠圖片會經 WhatsApp 發送（coupon），必須 JPEG——WhatsApp 會將 WebP 當 sticker
      const compressed = await smartCompressImage(file, { format: 'jpeg' });
      setCompressInfo({
        originalSize: compressed.originalSize,
        compressedSize: compressed.compressedSize,
        ratio: compressed.ratio,
      });
      setImageFile(compressed.file);

      const uploadForm = new FormData();
      uploadForm.append('file', compressed.file);

      const res = await fetch('/api/public/media/upload', {
        method: 'POST',
        body: uploadForm,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '上傳失敗');
      setImageUrl(data.url || '');
    } catch (err: any) {
      setError(err.message || '圖片上傳失敗');
      setImageFile(null);
      setImageUrl('');
    } finally {
      setUploading(false);
    }
  };

  const validate = (): boolean => {
    if (!form.restaurantName.trim()) { setError('請填寫餐廳名稱'); return false; }
    if (!form.title.trim()) { setError('請填寫優惠標題'); return false; }
    if (!form.description.trim()) { setError('請填寫優惠詳情'); return false; }
    if (!form.submitterName.trim()) { setError('請填寫聯絡人姓名'); return false; }
    if (!form.submitterPhone.trim()) { setError('請填寫聯絡電話——優惠批准後，你的商家中心會以此電話自動開通'); return false; }
    const verifyCode = form.merchantVerifyCode.trim();
    if (verifyCode && !/^\d{4}$/.test(verifyCode)) {
      setError('商家驗證碼必須為 4 位數字（建議公司電話最後 4 位）');
      return false;
    }
    if (!agreedTerms) { setError('請先閱讀並同意《商家服務協議》'); return false; }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        region: form.region,
        restaurantName: form.restaurantName,
        title: form.title,
        description: form.description,
        imageUrl,
        redeemInstructions: form.redeemInstructions || null,
        validUntil: form.validUntil ? Math.floor(new Date(`${form.validUntil}T23:59:59`).getTime() / 1000) : null,
        maxClaims: form.maxClaims ? Number(form.maxClaims) : null,
        merchantVerifyCode: form.merchantVerifyCode.trim() || null,
        submitterName: form.submitterName || null,
        submitterPhone: form.submitterPhone || null,
        submitterEmail: form.submitterEmail || null,
        // Consent evidence: checkbox state at submission time + agreement version
        agreedAt: Math.floor(Date.now() / 1000),
        agreementVersion: '1.0',
      };

      const res = await fetch('/api/public/merchant-offer-submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '提交失敗');

      setSuccess(true);
      setForm(EMPTY_FORM);
      setImageUrl('');
      setImageFile(null);
      setCompressInfo(null);
    } catch (err: any) {
      setError(err.message || '提交失敗');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-x1493-dark flex flex-col">
      <Header />

      <main className="flex-1 py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-stone-900 dark:text-white flex items-center justify-center gap-2">
              <Store className="text-x1493-primary" />
              商家提交食肆優惠
            </h1>
            <p className="text-stone-600 dark:text-stone-300 mt-2">
              填寫以下資料，我們會在審批後將你的優惠上架到 <a href="/offers" className="text-x1493-primary hover:underline">食肆優惠</a> 頁面。
            </p>
          </div>

          {success ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-green-200 dark:border-green-800 p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-stone-900 dark:text-white mb-2">提交成功！</h2>
              <p className="text-stone-600 dark:text-stone-300 mb-6">
                我們已收到你的優惠申請，審批通過後會盡快上架。如有查詢，歡迎 WhatsApp 聯絡我們。
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="/offers"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-x1493-primary text-white rounded-xl font-bold hover:bg-red-700 transition"
                >
                  查看現有優惠
                </a>
                <button
                  onClick={() => setSuccess(false)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-200 rounded-xl font-bold hover:bg-stone-100 dark:hover:bg-stone-700 transition"
                >
                  再提交一個
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-stone-200 dark:border-stone-700 p-6 sm:p-8 space-y-6">
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 dark:text-stone-200 mb-1">
                    <Store size={14} className="inline mr-1 -mt-0.5" />
                    餐廳名稱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="restaurantName"
                    value={form.restaurantName}
                    onChange={handleChange}
                    placeholder="例如 千歲擔擔麵"
                    className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-x1493-primary/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 dark:text-stone-200 mb-1">
                    <MapPinIcon />
                    地區 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="region"
                    value={form.region}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-x1493-primary/30"
                  >
                    <option value="local">香港本地</option>
                    <option value="mainland">內地</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-200 mb-1">
                  <Ticket size={14} className="inline mr-1 -mt-0.5" />
                  優惠標題 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="例如 全單 9 折 / 免費甜品一份"
                  className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-x1493-primary/30"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-stone-700 dark:text-stone-200">
                    <FileText size={14} className="inline mr-1 -mt-0.5" />
                    優惠詳情 / 細則 <span className="text-red-500">*</span>
                  </label>
                  <span className="text-xs text-stone-400">點擊模板快速填入</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {DESCRIPTION_TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => onTemplateClick('description', t.content)}
                      className="px-2.5 py-1 text-xs rounded-lg border border-stone-300 dark:border-stone-600 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 hover:border-x1493-primary transition"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="請詳細說明優惠內容、適用條件、使用限制等"
                  className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-x1493-primary/30"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-stone-700 dark:text-stone-200">
                    <Gift size={14} className="inline mr-1 -mt-0.5" />
                    兌換說明（可選）
                  </label>
                  <span className="text-xs text-stone-400">點擊模板快速填入</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {REDEEM_TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => onTemplateClick('redeemInstructions', t.content)}
                      className="px-2.5 py-1 text-xs rounded-lg border border-stone-300 dark:border-stone-600 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 hover:border-x1493-primary transition"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <textarea
                  name="redeemInstructions"
                  value={form.redeemInstructions}
                  onChange={handleChange}
                  rows={2}
                  placeholder="預設：出示此訊息同兌換碼畀店員，即可享用優惠。"
                  className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-x1493-primary/30"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-200 mb-2">
                  <Image size={14} className="inline mr-1 -mt-0.5" />
                  優惠圖片（可選）
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                {!imageUrl ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full h-40 border-2 border-dashed border-stone-300 dark:border-stone-600 rounded-xl flex flex-col items-center justify-center gap-2 text-stone-500 dark:text-stone-400 hover:border-x1493-primary hover:text-x1493-primary transition disabled:opacity-50"
                  >
                    {uploading ? <Loader2 className="w-8 h-8 animate-spin" /> : <Upload className="w-8 h-8" />}
                    <span>{uploading ? '上傳中...' : '點擊上傳優惠圖片'}</span>
                    <span className="text-xs">JPEG / PNG / GIF / WebP，最大 5MB</span>
                  </button>
                ) : (
                  <div className="relative rounded-xl overflow-hidden border border-stone-200 dark:border-stone-700 bg-stone-100 dark:bg-stone-900">
                    <img src={imageUrl} alt="優惠圖片預覽" className="w-full h-48 object-cover" />
                    <button
                      type="button"
                      onClick={() => { setImageUrl(''); setImageFile(null); setCompressInfo(null); fileInputRef.current && (fileInputRef.current.value = ''); }}
                      className="absolute top-2 right-2 px-3 py-1.5 bg-black/60 text-white text-xs rounded-lg hover:bg-black/80 transition"
                    >
                      移除
                    </button>
                    {compressInfo && compressInfo.ratio !== '0%' && (
                      <div className="absolute bottom-2 left-2 px-3 py-1.5 bg-green-600/90 text-white text-xs rounded-lg">
                        {formatSize(compressInfo.originalSize)} → {formatSize(compressInfo.compressedSize)}（{compressInfo.ratio}）
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border border-stone-200 dark:border-stone-700 rounded-xl p-4 bg-stone-50/50 dark:bg-stone-800/30">
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-200 mb-1 flex items-center gap-1">
                  <ShieldCheck size={14} className="text-purple-600" />
                  商家驗證碼（可選，4 位數字）
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="text"
                    name="merchantVerifyCode"
                    inputMode="numeric"
                    maxLength={4}
                    value={form.merchantVerifyCode}
                    onChange={(e) => setForm((prev) => ({ ...prev, merchantVerifyCode: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    placeholder="例如 1493"
                    className="w-36 px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white text-center font-mono text-lg tracking-widest focus:ring-2 focus:ring-x1493-primary/30"
                  />
                  <a
                    href="/media/media/redemption.mp4"
                    target="_blank"
                    rel="noreferrer"
                    title="教學影片（新視窗播放）"
                    className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl text-sm text-x1493-primary hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-x1493-primary transition font-medium"
                  >
                    🎬 教學影片
                    <span className="font-mono text-xs text-stone-400">/media/media/redemption.mp4</span>
                  </a>
                </div>
                <p className="text-xs text-stone-400 mt-2">
                  建議填寫餐廳公司電話的最後 4 位。設定後，顧客領取優惠會收到電子兌換頁（QR Code），店員掃碼並輸入此碼即可自助核銷，唔使逐筆人手確認。留空將採用平台預設驗證碼。
                </p>
                <div className="mt-3 rounded-xl overflow-hidden border border-stone-200 dark:border-stone-700 bg-black">
                  <video
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full max-h-[400px]"
                    src="/media/media/redemption.mp4"
                  >
                    你的瀏覽器不支援影片播放。
                  </video>
                </div>
                <p className="text-xs text-stone-400 mt-1.5 flex items-center gap-1">
                  🎬 30 秒教學：顧客出示 QR Code → 店員掃碼 → 輸入驗證碼，核銷完成
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 dark:text-stone-200 mb-1">
                    <Calendar size={14} className="inline mr-1 -mt-0.5" />
                    有效期至（可選）
                  </label>
                  <input
                    type="date"
                    name="validUntil"
                    value={form.validUntil}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-x1493-primary/30"
                  />
                  <p className="text-xs text-stone-400 mt-1">留空代表長期有效</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 dark:text-stone-200 mb-1">
                    名額（可選）
                  </label>
                  <input
                    type="number"
                    name="maxClaims"
                    min={1}
                    value={form.maxClaims}
                    onChange={handleChange}
                    placeholder="留空 = 不限"
                    className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-x1493-primary/30"
                  />
                </div>
              </div>

              <div className="border-t border-stone-200 dark:border-stone-700 pt-6">
                <h3 className="text-lg font-bold text-stone-900 dark:text-white mb-1">聯絡資料</h3>
                <p className="text-xs text-stone-400 mb-4">優惠批准後，你的商家中心會以聯絡電話自動開通（WhatsApp 專屬連結會發送至此號碼）。</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-200 mb-1">
                      <User size={14} className="inline mr-1 -mt-0.5" />
                      聯絡人 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="submitterName"
                      value={form.submitterName}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-x1493-primary/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-200 mb-1">
                      <Phone size={14} className="inline mr-1 -mt-0.5" />
                      電話（WhatsApp）<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="submitterPhone"
                      value={form.submitterPhone}
                      onChange={handleChange}
                      placeholder="例如 6xxxxxxxx"
                      className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-x1493-primary/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-200 mb-1">
                      <Mail size={14} className="inline mr-1 -mt-0.5" />
                      電郵
                    </label>
                    <input
                      type="email"
                      name="submitterEmail"
                      value={form.submitterEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-x1493-primary/30"
                    />
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="mt-1 accent-red-600 w-4 h-4 shrink-0"
                />
                <span className="text-sm text-stone-600 dark:text-stone-300">
                  我已閱讀並同意
                  <a href="/merchant/agreement" target="_blank" rel="noopener noreferrer" className="text-x1493-primary hover:underline font-medium">《商家服務協議》</a>
                  ，並同意香港辣谷按
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-x1493-primary hover:underline font-medium">《隱私條款》</a>
                  處理本表格所載的資料。 <span className="text-red-500">*</span>
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting || uploading}
                className="w-full py-3.5 bg-x1493-primary text-white rounded-xl font-bold text-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                {submitting ? '提交中...' : '提交優惠申請'}
              </button>

              <p className="text-xs text-stone-500 dark:text-stone-400 text-center">
                提交後需經管理員審批，審批通過後會自動上架。請確保圖片及內容符合平台規則。
              </p>
            </form>
          )}
        </div>
      </main>

      {/* Template apply dialog */}
      {pendingTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-stone-900 dark:text-white mb-2">套用模板</h3>
            <p className="text-sm text-stone-600 dark:text-stone-300 mb-4">
              此欄位已有內容，你想覆蓋現有內容，還是追加到末尾？
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setPendingTemplate(null)}
                className="px-4 py-2 border border-stone-300 dark:border-stone-600 rounded-lg text-sm text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => applyTemplate(pendingTemplate.field, pendingTemplate.content, 'append')}
                className="px-4 py-2 bg-stone-100 dark:bg-stone-700 text-stone-700 dark:text-stone-200 rounded-lg text-sm hover:bg-stone-200 dark:hover:bg-stone-600"
              >
                追加
              </button>
              <button
                type="button"
                onClick={() => applyTemplate(pendingTemplate.field, pendingTemplate.content, 'replace')}
                className="px-4 py-2 bg-x1493-primary text-white rounded-lg text-sm hover:bg-red-700"
              >
                覆蓋
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function MapPinIcon() {
  // Lucide MapPin inline to avoid extra import in this file
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="inline mr-1 -mt-0.5"
    >
      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
